import React, { memo } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Colors, ModernPalette, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

export type ModernCardVariant = 'default' | 'elevated' | 'outlined';
export type ModernCardPadding = 'sm' | 'md' | 'lg';

export type ModernCardProps = {
  children: React.ReactNode;
  variant?: ModernCardVariant;
  padding?: ModernCardPadding;
  accentColor?: string;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

const PADDING_VALUES: Record<ModernCardPadding, number> = {
  sm: Spacing.md,   // 12px
  md: Spacing.xl,   // 20px
  lg: Spacing.xxl,  // 24px
};

const CARD_BORDER_RADIUS = BorderRadius.md; // 12px
const ACCENT_BORDER_WIDTH = 3;

/**
 * ModernCard - Clean, minimal card component
 *
 * Features:
 * - Three variants: default (subtle shadow), elevated (stronger shadow), outlined (border)
 * - Optional top accent border for visual categorization
 * - Consistent border radius (12px)
 * - Theme-aware backgrounds
 * - Optional press handler with opacity feedback
 */
function ModernCardComponent({
  children,
  variant = 'default',
  padding = 'md',
  accentColor,
  onPress,
  style,
  testID,
}: ModernCardProps) {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';

  const palette = Colors[colorScheme ?? 'light'];
  const backgroundColor = isDark ? palette.backgroundLight : ModernPalette.white;
  const borderColor = isDark ? palette.border : ModernPalette.borderMedium;

  const variantStyle: ViewStyle = (() => {
    switch (variant) {
      case 'elevated':
        return Shadows.md;
      case 'outlined':
        return {
          borderWidth: 1,
          borderColor,
        };
      case 'default':
      default:
        return Shadows.sm;
    }
  })();

  const containerStyle: ViewStyle = {
    backgroundColor,
    padding: PADDING_VALUES[padding],
    borderRadius: CARD_BORDER_RADIUS,
    ...(accentColor && {
      borderTopWidth: ACCENT_BORDER_WIDTH,
      borderTopColor: accentColor,
    }),
  };

  const Wrapper = onPress ? TouchableOpacity : View;

  return (
    <Wrapper
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
      style={[styles.container, variantStyle, containerStyle, style]}
      testID={testID}
    >
      {children}
    </Wrapper>
  );
}

export const ModernCard = memo(ModernCardComponent);

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
});
